import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useLanguage } from '../i18n/LanguageContext';

interface Props {
  src: string;
  poster?: string;
  controls?: boolean;
}

function setupMseWs(video: HTMLVideoElement, wsUrl: string): () => void {
  let stopped = false;
  let sb: SourceBuffer | null = null;
  let ms: MediaSource | null = null;
  const queue: ArrayBuffer[] = [];
  let flushing = false;

  const flush = () => {
    if (!sb || flushing || sb.updating || !queue.length || ms?.readyState !== 'open') return;
    flushing = true;
    const chunk = queue.shift()!;
    try { sb.appendBuffer(chunk); }
    catch (e: any) {
      flushing = false;
      if (e?.name === 'QuotaExceededError' && sb.buffered.length) {
        try { sb.remove(sb.buffered.start(0), Math.max(0, video.currentTime - 2)); } catch { /* ignore */ }
        queue.unshift(chunk); // retry this chunk after the remove's updateend
      } else {
        queue.length = 0;
      }
    }
  };

  const ws = new WebSocket(wsUrl);
  ws.binaryType = 'arraybuffer';

  // go2rtc MSE WebSocket protocol: client sends codec offer first, server replies with the codec it'll use.
  ws.onopen = () => {
    ws.send(JSON.stringify({
      type: 'mse',
      value: 'avc1.640029,avc1.42E01F,hvc1.1.6.L93.B0,mp4a.40.2',
    }));
  };

  ws.onmessage = (ev) => {
    if (stopped) return;
    if (typeof ev.data === 'string') {
      let msg: { type: string; value?: string };
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type !== 'mse') return;

      ms = new MediaSource();
      const objUrl = URL.createObjectURL(ms);
      video.src = objUrl;

      ms.addEventListener('sourceopen', () => {
        URL.revokeObjectURL(objUrl);
        if (stopped) return;
        const mime = msg.value || 'video/mp4';
        try { sb = ms!.addSourceBuffer(mime); }
        catch { return; }
        sb.mode = 'segments';

        sb.addEventListener('updateend', () => {
          flushing = false;
          try {
            if (sb!.buffered.length) {
              const behind = video.currentTime - sb!.buffered.start(0);
              if (behind > 10) {
                sb!.remove(sb!.buffered.start(0), video.currentTime - 5);
                return;
              }
            }
          } catch { /* ignore */ }
          flush();
        });

        flush(); // drain anything queued before sourceopen
      }, { once: true });
    } else if (ev.data instanceof ArrayBuffer) {
      queue.push(ev.data);
      flush();
    }
  };

  return () => {
    stopped = true;
    ws.close();
    if (ms?.readyState === 'open') { try { ms.endOfStream(); } catch {} }
    video.src = '';
  };
}

export const CameraPlayer: React.FC<Props> = ({ src, poster, controls = false }) => {
  const videoRef    = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef      = useRef<Hls | null>(null);
  const mseCleanup  = useRef<(() => void) | null>(null);
  const { t } = useLanguage();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buffering, setBuffering] = useState(true);

  useEffect(() => {
    const onChange = () => {
      const doc = document as any;
      setIsFullscreen(!!(doc.fullscreenElement || doc.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    hlsRef.current?.destroy(); hlsRef.current = null;
    mseCleanup.current?.(); mseCleanup.current = null;
    setBuffering(true);

    const clearBuffering = () => setBuffering(false);
    const onCanPlay = () => { video.play().catch(() => {}); };
    video.addEventListener('timeupdate', clearBuffering);
    video.addEventListener('playing', clearBuffering);
    video.addEventListener('canplay', onCanPlay);

    const isWs = src.startsWith('ws://') || src.startsWith('wss://');
    const isHls = src.includes('.m3u8');

    if (isWs && 'MediaSource' in window) {
      mseCleanup.current = setupMseWs(video, src);
    } else if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false,
        liveSyncDurationCount: 3, liveMaxLatencyDurationCount: 6 });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
    } else {
      // Progressive file (event clip .mp4) or native HLS on Safari.
      video.src = src;
      video.load();
    }

    return () => {
      video.removeEventListener('timeupdate', clearBuffering);
      video.removeEventListener('playing', clearBuffering);
      video.removeEventListener('canplay', onCanPlay);
      hlsRef.current?.destroy(); hlsRef.current = null;
      mseCleanup.current?.(); mseCleanup.current = null;
    };
  }, [src]);

  const handleFullscreen = () => {
    const doc = document as any;
    if (isFullscreen) {
      if (doc.exitFullscreen) doc.exitFullscreen();
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
    } else {
      const el = containerRef.current as any;
      if (!el) return;
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        bgcolor: 'black',
        aspectRatio: '16/9',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        controls={controls}
        poster={poster}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      {buffering && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.45)',
        }}>
          <CircularProgress size={40} sx={{ color: 'white' }} />
        </Box>
      )}
      {!controls && (
        <IconButton
          onClick={handleFullscreen}
          aria-label={isFullscreen ? t.a11yExitFullscreen : t.a11yEnterFullscreen}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
          }}
        >
          {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
        </IconButton>
      )}
    </Box>
  );
};
